import { ExpandableTableRow } from '../NewVerificationTab.data';

/**
 * Property under which a row's stable id is carried on the row object itself. Editing a cell
 * replaces the row with a spread copy (`{ ...oldRow, [col]: value }`) in the parent's onRowUpdate,
 * which produces a NEW object reference. A WeakMap keyed on object identity therefore hands the
 * edited row a fresh id, and any row-scoped state keyed by that id (the session-edit highlight)
 * lands on the wrong key and silently disappears.
 *
 * Carrying the id as a property on the row means the spread copy keeps the same id, so the
 * highlight follows the edit — while genuinely new rows (constructed fresh, not spread) still get
 * their own id. The id must be enumerable so the parent's `{ ...oldRow }` spread copies it; this is
 * safe because request builders (buildPayeeVerificationRequest, buildManualPayeeVerificationRequest,
 * buildAllAccountRequests) read named fields rather than spreading the whole row, so the extra
 * property never reaches the wire, and populate*Rows rebuild rows from server data (fresh id).
 */
const ROW_ID_KEY = '__verificationRowId';

interface RowWithId {
  [ROW_ID_KEY]?: number;
}

/**
 * Creates a resolver that assigns each row a stable numeric id and returns the same id for the
 * same logical row across re-renders and spread-copy edits.
 *
 * - First sight of a row: assign the next id and stamp it onto the row.
 * - A spread copy of a stamped row already carries the id, so it resolves to the same value.
 *
 * The counter is advanced past any carried id it sees: a fresh resolver can inherit already-stamped
 * rows (e.g. a duplicated account section spreads its rows into a new GenericTable), and without
 * seeding the counter it would re-issue those ids to later unstamped rows and collide.
 */
export function createRowIdResolver(): (row: ExpandableTableRow) => number {
  let nextRowId = 0;

  return (row: ExpandableTableRow): number => {
    const carried = (row as RowWithId)[ROW_ID_KEY];
    if (carried !== undefined) {
      nextRowId = Math.max(nextRowId, carried + 1);
      return carried;
    }

    const id = nextRowId++;
    // Stamp the id onto the row so a later spread copy (the edit path) carries it forward.
    (row as RowWithId)[ROW_ID_KEY] = id;
    return id;
  };
}
